import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import PageHeader from "../../../../components/common/PageHeader";
import PageLoader from "../../../../components/common/PageLoader";
import ChipList from "../../../../components/common/ChipList";
import { tenantPermissionService } from "../../services";
import {
  collectPermissionSuggestions,
  mergePermissionDetail,
  normalizePermissionList,
} from "../../utils/normalizePermission";
import PermissionFormDialog from "../PermissionFormDialog";
import { appActions } from "../../../../redux/actions";
import { getApiErrorMessage } from "../../../../helpers";
import useAuth from "../../../../hooks/useAuth";
import { tenantRoleService } from "../../../role-management/services";
import { normalizeRoleList } from "../../../role-management/utils/normalizeRole";

const DetailItem = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ fontWeight: 600, mt: 0.25, wordBreak: "break-word" }}
    >
      {value || "—"}
    </Typography>
  </Box>
);

const PermissionDetailsPage = () => {
  const { permissionId: permissionParam } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { activeTenantId, tenantId: authTenantId } = useAuth();
  const tenantId = activeTenantId || authTenantId;
  const listPath = location.pathname.startsWith("/tenant-")
    ? "/tenant-permissions"
    : "/permissions";

  const [permission, setPermission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [allPermissions, setAllPermissions] = useState([]);
  const [linkedRoles, setLinkedRoles] = useState([]);
  const [linksLoading, setLinksLoading] = useState(false);

  const suggestions = useMemo(
    () => collectPermissionSuggestions(allPermissions),
    [allPermissions]
  );

  const loadPermission = useCallback(async () => {
    if (!permissionParam) return;
    setLoading(true);
    setError("");
    try {
      const decoded = decodeURIComponent(permissionParam);
      const fromState = location.state?.permission || null;
      const seedName =
        fromState?.name ||
        (String(fromState?.permissionId) === decoded
          ? fromState?.name
          : null) ||
        decoded;

      let detail = null;
      try {
        detail = await tenantPermissionService.getTenantPermissionByName(
          seedName,
          tenantId
        );
      } catch {
        detail = null;
      }

      if (!detail && !fromState) {
        throw new Error("Permission not found.");
      }

      setPermission(
        mergePermissionDetail(fromState || { name: seedName }, detail || fromState)
      );
    } catch (err) {
      setPermission(null);
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [location.state, permissionParam, tenantId]);

  useEffect(() => {
    loadPermission();
  }, [loadPermission]);

  const openEdit = async () => {
    setEditOpen(true);
    if (allPermissions.length) return;
    try {
      setAllPermissions(
        normalizePermissionList(
          await tenantPermissionService.getAllTenantPermissions(tenantId)
        )
      );
    } catch {
      setAllPermissions([]);
    }
  };

  const handleSave = async (payload) => {
    if (!permission) return;
    setSaving(true);
    try {
      await tenantPermissionService.updateTenantPermission(
        permission,
        payload,
        tenantId
      );
      dispatch(appActions.apiSuccessMessage("Permission updated successfully."));
      setEditOpen(false);
      await loadPermission();
    } catch (err) {
      throw new Error(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const openDelete = async () => {
    setDeleteOpen(true);
    setLinkedRoles([]);
    setLinksLoading(true);
    try {
      const permissionName = String(permission?.name || "").trim().toLowerCase();
      if (!permissionName) {
        setLinkedRoles([]);
        return;
      }
      const roles = normalizeRoleList(
        await tenantRoleService.getAllTenantRoles(tenantId)
      );
      setLinkedRoles(
        roles
          .filter((role) =>
            (role.allow || []).some(
              (item) => String(item).trim().toLowerCase() === permissionName
            )
          )
          .map((role) => String(role.name || role.roleId || "").trim())
          .filter(Boolean)
      );
    } catch {
      setLinkedRoles([]);
    } finally {
      setLinksLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!permission?.name) return;
    setSaving(true);
    try {
      await tenantRoleService.detachPermissionFromTenantRoles(
        permission,
        tenantId
      );
      await tenantPermissionService.deleteTenantPermission(
        permission.name,
        tenantId
      );
      dispatch(appActions.apiSuccessMessage("Permission deleted successfully."));
      setDeleteOpen(false);
      navigate(listPath);
    } catch (err) {
      dispatch(appActions.apiErrorMessage(getApiErrorMessage(err)));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <PageLoader text="Loading permission..." />;
  }

  if (error && !permission) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackOutlinedIcon />}
          onClick={() => navigate(listPath)}
        >
          Back to permissions
        </Button>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!permission) return null;

  return (
    <Box sx={{ width: "100%" }}>
      <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
        <Button
          startIcon={<ArrowBackOutlinedIcon />}
          onClick={() => navigate(listPath)}
        >
          Back to permissions
        </Button>
      </Stack>
      <PageHeader
        title="Permission Details"
        subtitle={permission.name || permission.permissionId}
      />

      <Paper
        elevation={0}
        sx={{ p: 3, border: "1px solid", borderColor: "divider", mb: 2 }}
      >
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Basic Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <DetailItem
                  label="Permission ID"
                  value={permission.permissionId}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DetailItem label="Permission Name" value={permission.name} />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Allow
            </Typography>
            <ChipList
              items={permission.allow}
              maxVisible={null}
              emptyLabel="No allow rules"
              sx={{ flexWrap: "wrap", rowGap: 0.75, overflow: "visible" }}
            />
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              Deny
            </Typography>
            <ChipList
              items={permission.deny}
              maxVisible={null}
              emptyLabel="No deny rules"
              sx={{ flexWrap: "wrap", rowGap: 0.75, overflow: "visible" }}
            />
          </Box>
        </Stack>
      </Paper>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.25}
        flexWrap="wrap"
        useFlexGap
      >
        <Button variant="contained" onClick={openEdit}>
          Edit Permission
        </Button>
        <Button
          variant="outlined"
          color="error"
          onClick={openDelete}
        >
          Delete Permission
        </Button>
      </Stack>

      <PermissionFormDialog
        open={editOpen}
        mode="edit"
        permission={permission}
        suggestions={suggestions}
        loading={saving}
        onClose={() => setEditOpen(false)}
        onSubmit={handleSave}
      />

      <Dialog
        open={deleteOpen}
        onClose={saving ? undefined : () => setDeleteOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Delete Permission</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Delete permission{" "}
            <strong>{permission.name || permission.permissionId}</strong>? This
            cannot be undone.
          </Typography>
          {linksLoading ? (
            <Typography variant="body2" color="text.secondary">
              Checking where this permission is used...
            </Typography>
          ) : linkedRoles.length ? (
            <Alert severity="warning" sx={{ mt: 0.5 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                This permission is still assigned to{" "}
                <strong>{linkedRoles.length}</strong> role
                {linkedRoles.length === 1 ? "" : "s"}. Deleting it does not
                remove it from those roles on the server. Update the roles below
                after delete so they no longer reference this permission.
              </Typography>
              <Typography variant="body2" component="div">
                Associated roles:
                <Box component="ul" sx={{ m: 0, pl: 2.5, mt: 0.5 }}>
                  {linkedRoles.map((name) => (
                    <li key={name}>{name}</li>
                  ))}
                </Box>
              </Typography>
            </Alert>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No roles currently list this permission.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={saving || linksLoading}
          >
            {saving ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PermissionDetailsPage;
